import { Box } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { toNumber } from 'shared/lib/number/number';
import {
    formatReportAxisCurrency,
    formatReportCurrency,
} from 'shared/lib/reportFormatters';
import {
    ComposableReportWidget,
    type ReportWidgetColumnConfig,
    type ReportWidgetConfig,
    type ReportWidgetInteractionPayload,
    type ReportWidgetItem,
    type ReportWidgetTooltipRowConfig,
    type ReportWidgetTooltipRowsBuilder,
} from 'widgets/ComposableReportWidget';
import {
    buildSpendRow,
    getTooltipPointFromPayload,
} from 'widgets/ComposableReportWidget/model/lib/tooltipHelpers';

import type { SpendCategoryRowDto } from 'entities/ReportSpendSummary';

const TOP_CATEGORIES_COUNT = 10;

type SpendSummaryCategoriesTableColumnId =
    | 'category'
    | 'orders'
    | 'items'
    | 'totalSpend'
    | 'spend'
    | 'percentOfTotal'
    | 'percentSpend';

function categoryToWidgetItem(
    dto: SpendCategoryRowDto,
    index: number,
    uncategorizedLabel: string,
): ReportWidgetItem {
    const id = dto.categoryId ?? `category-${index}`;
    const label = dto.categoryTitle?.trim() || uncategorizedLabel;
    const spendNum = toNumber(dto.totalSpend);
    return {
        id,
        label,
        primaryValue: spendNum,
        secondaryValues: {
            label,
            totalSpend: dto.totalSpend,
            orderCount: dto.orderCount,
            itemCount: dto.itemCount,
            percentOfTotal: dto.percentOfTotal,
        },
        meta: { categoryId: dto.categoryId, categoryTitle: dto.categoryTitle },
    };
}

interface SpendSummaryCategoriesSectionProps {
    byCategory: SpendCategoryRowDto[];
    loading?: boolean;
    disableChartAnimation?: boolean;
    showBottomBorder?: boolean;
    tableOnly?: boolean;
    tableColumnIds?: SpendSummaryCategoriesTableColumnId[];
    onCategorySelect?: (payload: ReportWidgetInteractionPayload) => void;
}

export const SpendSummaryCategoriesSection = memo(
    ({
        byCategory,
        loading = false,
        disableChartAnimation = false,
        showBottomBorder = true,
        tableOnly = false,
        tableColumnIds,
        onCategorySelect,
    }: SpendSummaryCategoriesSectionProps) => {
        const { t } = useTranslation();

        const items: ReportWidgetItem[] = useMemo(() => {
            const uncategorizedLabel = t('spendSummary.uncategorized');
            const sorted = [...byCategory]
                .filter((category) => toNumber(category.totalSpend) > 0)
                .sort(
                    (a, b) => toNumber(b.totalSpend) - toNumber(a.totalSpend),
                );
            const visible = tableOnly
                ? sorted
                : sorted.slice(0, TOP_CATEGORIES_COUNT);

            return visible.map((dto, i) =>
                categoryToWidgetItem(dto, i, uncategorizedLabel),
            );
        }, [byCategory, t, tableOnly]);

        const handleInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                onCategorySelect?.(payload);
            },
            [onCategorySelect],
        );

        const tableColumns = useMemo<ReportWidgetColumnConfig[]>(() => {
            const allColumnsById: Record<
                SpendSummaryCategoriesTableColumnId,
                ReportWidgetColumnConfig
            > = {
                category: {
                    id: 'category',
                    label: t('Category'),
                    valueKey: 'label',
                    format: 'text',
                    align: 'left',
                    bold: true,
                    minWidth: 200,
                    allowWrap: true,
                },
                orders: {
                    id: 'orders',
                    label: t('Orders'),
                    valueKey: 'orderCount',
                    format: 'number',
                    align: 'right',
                },
                items: {
                    id: 'items',
                    label: t('Items'),
                    valueKey: 'itemCount',
                    format: 'number',
                    align: 'right',
                },
                totalSpend: {
                    id: 'totalSpend',
                    label: t('spendSummary.totalSpend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    align: 'right',
                },
                spend: {
                    id: 'spend',
                    label: t('spendSummary.spend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    align: 'right',
                },
                percentOfTotal: {
                    id: 'percentOfTotal',
                    label: t('spendSummary.percentOfTotal'),
                    valueKey: 'percentOfTotal',
                    format: 'percent',
                    align: 'right',
                },
                percentSpend: {
                    id: 'percentSpend',
                    label: t('spendSummary.percentSpend'),
                    valueKey: 'percentOfTotal',
                    format: 'percent',
                    align: 'right',
                },
            };

            const defaultOrder: SpendSummaryCategoriesTableColumnId[] = [
                'category',
                'orders',
                'items',
                'totalSpend',
                'percentOfTotal',
            ];

            const orderedIds = tableColumnIds?.length
                ? tableColumnIds
                : defaultOrder;

            return orderedIds
                .map((id) => allColumnsById[id])
                .filter((column): column is ReportWidgetColumnConfig =>
                    Boolean(column),
                );
        }, [t, tableColumnIds]);

        const config: ReportWidgetConfig = useMemo(() => {
            const tooltipRowsBuilder: ReportWidgetTooltipRowsBuilder = (
                args,
            ) => {
                const resolved = getTooltipPointFromPayload(args);
                const item = resolved?.item;

                if (!item) {
                    return null;
                }

                const spendNum = toNumber(item.primaryValue);
                const tooltipRows: ReportWidgetTooltipRowConfig[] = [
                    {
                        id: 'header',
                        variant: 'header',
                        label: item.label,
                        boldLabel: true,
                        mutedLabel: true,
                        marginBottom: 4,
                    },
                    buildSpendRow({
                        id: 'spend',
                        label: t('spendSummary.spend'),
                        amount: spendNum,
                        percent: item.secondaryValues?.percentOfTotal,
                        total: args.total,
                        valueFormatter: args.valueFormatter,
                        valueColor: resolved.color ?? 'metrics.base',
                    }),
                ];

                return tooltipRows;
            };

            return {
                widgetId: 'spend-summary-categories',
                title: t('spendSummary.categoriesSectionTitle'),
                titleVariant: tableOnly ? 'section' : 'report',
                layout: tableOnly ? 'stacked' : undefined,
                visualization: {
                    enabled: !tableOnly,
                    type: 'bar',
                    options: {
                        colors: ['hsl(221, 83%, 53%)'],
                        showLegend: false,
                        valueFormatter: formatReportCurrency,
                        yAxisTickFormatter: formatReportAxisCurrency,
                        xAxisTickWrap: {
                            maxLines: 2,
                            maxCharsPerLine: 22,
                        },
                        tooltipRowsBuilder,
                    },
                },
                pagination: tableOnly
                    ? {
                          mode: 'client',
                          pageSize: 5,
                          revealAllOnLoadMore: true,
                      }
                    : undefined,
                table: {
                    enabled: tableOnly,
                    columns: tableColumns,
                    options: {
                        loadMoreVariant: 'link',
                    },
                },
                onRowClick: onCategorySelect ? handleInteraction : undefined,
                onChartItemClick:
                    !tableOnly && onCategorySelect
                        ? handleInteraction
                        : undefined,
            };
        }, [t, tableColumns, tableOnly, onCategorySelect, handleInteraction]);

        return (
            <Box
                data-testclass="spend-summary-categories-section"
                data-pdf-section="true"
                sx={{
                    borderBottom: showBottomBorder
                        ? '1px solid #E0E0E0'
                        : 'none',
                    paddingBottom: showBottomBorder ? '40px' : 0,
                }}
            >
                <ComposableReportWidget
                    config={config}
                    items={items}
                    loading={loading}
                    isPrinting={disableChartAnimation}
                    disableChartAnimation={disableChartAnimation}
                />
            </Box>
        );
    },
);

SpendSummaryCategoriesSection.displayName = 'SpendSummaryCategoriesSection';
